import type { GateContext, Lesson } from '../types';
import { f, run } from './helpers';

const SNAP_T = 0.5;
const snap = (ctx: GateContext) => ctx.sample('m1-snapshot', SNAP_T);
const plantOf = (ctx: GateContext) => run(ctx, 'm1-snapshot').config.plant;

function accel(ctx: GateContext) {
  const s = snap(ctx);
  const p = plantOf(ctx);
  return (s.T - p.m * p.g - p.b * s.v - p.k * (s.z - p.zs) + s.d) / p.m;
}

export const m1: Lesson = {
  id: 'm1',
  number: 1,
  title: 'Describe the state',
  summary: 'In this module, you will turn real-world physical forces into simple equations of motion, and see how engineers package them into state-space matrices for flight computers.',
  objectives: [
    'Explain a derivative as a rate of change.',
    'Write the force balance with every force and its sign.',
    'Read the two scalar state equations and their matrix form row by row.',
    'Explain what balances gravity at hover.',
  ],
  controls: ['manual', 'acceleration'],
  controllerKinds: ['manual'],
  sections: [
    {
      id: 'm1-notation',
      title: 'Notation used in this course',
      phase: 'learn',
      blocks: [
        {
          type: 'p',
          text: 'Control engineering bridges the physical world of spinning rotors and gravity with flight computer algorithms using mathematical shorthand. Aerospace engineers rely on standardized notation so they can express complex physical dynamics compactly and unambiguously. Becoming comfortable with this notation ensures you can easily translate flight controller specifications, research papers, and flight software into working code.',
        },
        {
          type: 'p',
          text: 'Before diving into the equations of motion, here is the shorthand you will encounter throughout this course. Each symbol represents a concrete physical quantity that you can inspect in the simulator.',
        },
        { type: 'widget', widget: 'notation-primer' },
      ],
    },
    {
      id: 'm1-rates',
      title: 'Rates: the derivative as how fast',
      phase: 'learn',
      blocks: [
        {
          type: 'p',
          text: 'Drones cannot teleport; flight is continuous motion through space and time. A flight controller cannot react only to where an aircraft is right now. It must continuously know how fast the craft is moving and how rapidly its speed is changing so it can anticipate where it will be in the next fraction of a second.',
        },
        {
          type: 'p',
          text: 'In physics and mathematics, a **{{derivative}}** is simply a rate of change over time. If a drone’s altitude goes from 2.00 m to 2.01 m in 0.01 s, it is traveling at 1 m/s. That rate of change of position is its **{{v|velocity}}**. If velocity is changing, the rate of change of velocity is its **{{a|acceleration}}**.',
        },
        {
          type: 'p',
          text: 'In control engineering, placing a dot above a letter (pronounced "z-dot" and "v-dot") is standard shorthand for its time derivative ($\\,\\dot{}\\ \\equiv d/dt$):',
        },
        {
          type: 'math',
          tex: '\\term{dot}{\\dot z} = \\term{v}{v} \\qquad \\term{dot}{\\dot v} = \\term{a}{a}',
          aria: 'z dot equals v, and v dot equals a: the rate of change of altitude is velocity, and the rate of change of velocity is acceleration.',
        },
        {
          type: 'p',
          text: 'These two definitions are fundamental: position changes at the rate of velocity ($\\dot z = v$), and velocity changes at the rate of acceleration ($\\dot v = a$). Together, they form the kinematic bridge from forces to motion.',
        },
        { type: 'widget', widget: 'derivative-rate' },
      ],
    },
    {
      id: 'm1-forces',
      title: 'Every force, with its sign',
      phase: 'learn',
      blocks: [
        {
          type: 'p',
          text: 'To control any flying vehicle, a flight controller must understand what causes it to accelerate, decelerate, or hover. Newton’s second law provides the foundation: **mass × acceleration = net force** ($m\\,a = \\sum F$). By tallying up every push and pull along the vertical axis, we can predict exactly how the vehicle will respond.',
        },
        {
          type: 'p',
          text: 'Before doing math, we establish a sign convention: **upward is positive**. Any force pushing up adds speed and gets a plus sign; any force pulling down subtracts speed and gets a minus sign.',
        },
        {
          type: 'math',
          tex: '\\term{m}{m}\\,\\term{a}{a} = \\term{T}{T} - \\term{m}{m}\\,\\term{g}{g} - \\term{b}{b}\\,\\term{v}{v} - \\term{k}{k}\\,(\\term{z}{z} - \\term{zs}{z_s}) + \\term{disturbance}{d}',
          aria: 'Mass m times acceleration a equals thrust T, minus weight m g, minus damping b times velocity v, minus stiffness k times the quantity z minus z sub s, plus disturbance d.',
        },
        {
          type: 'p',
          text: 'Here is what each term represents and why its sign is assigned that way:',
        },
        {
          type: 'list',
          items: [
            '**Rotor thrust** ($\\term{T}{T}$): Propellers spin to blow air downward, creating an upward reaction force that lifts the vehicle. Because it points up, it enters with a plus sign ($+T$).',
            '**Gravity / weight** ($-m\\,g$): The Earth pulls the mass downward with force $m \\times g$. Because it always points down, it enters with a minus sign ($-m\\,g$).',
            '**Aerodynamic damping** ($-b\\,v$): Air resistance opposes whichever way the vehicle is traveling. When moving up ($v > 0$), drag pushes down ($-b\\,v < 0$). When sinking down ($v < 0$), drag pushes up ($-b\\,v > 0$). The minus sign guarantees that damping always opposes motion.',
            '**Tether spring** ($-k\\,(z - z_s)$): Our training drone is tethered by an elastic spring with stiffness {{k}} anchored at rest altitude {{zs}}. When above rest ($z > z_s$), the stretched spring pulls downward; when below ($z < z_s$), it pushes upward. Parentheses mean we subtract $(z - z_s)$ first to measure stretch, and the minus sign pulls the drone back toward $z_s$. (A free-flying drone has no tether, so $k = 0$).',
            '**External disturbance** ({{disturbance|d}}): Any unexpected environmental force, defined as positive upward. For example, carrying a 1 N hanging package is represented as a downward load with $d = -1\\text{ N}$, while an upward gust of wind is positive.',
          ],
        },
        {
          type: 'p',
          text: 'Try testing these forces with real numbers below. Adjust the sliders or click **Copy current simulator snapshot** to see each force component and how their sum sets the vehicle’s acceleration.',
        },
        { type: 'widget', widget: 'force-balance' },
      ],
    },
    {
      id: 'm1-scalar',
      title: 'Two state equations',
      phase: 'learn',
      blocks: [
        {
          type: 'p',
          text: 'Knowing forces at a single instant in time is useful, but flight software needs to predict how motion unfolds across time. A digital computer cannot jump directly from force to future position in one leap; it must step forward in time by tracking instantaneous **rates of change**.',
        },
        {
          type: 'p',
          text: 'To find how quickly velocity is changing, divide both sides of the force balance by mass $m$. Because acceleration is the rate of change of velocity ($\\dot v = a$), we get two fundamental rates that completely describe the vehicle’s motion:',
        },
        {
          type: 'math',
          tex: '\\begin{aligned}\\term{dot}{\\dot z} &= \\term{v}{v} \\\\ \\term{dot}{\\dot v} &= \\frac{\\term{T}{T} - \\term{m}{m}\\,\\term{g}{g} - \\term{b}{b}\\,\\term{v}{v} - \\term{k}{k}\\,(\\term{z}{z} - \\term{zs}{z_s}) + \\term{disturbance}{d}}{\\term{m}{m}}\\end{aligned}',
          aria: 'z dot equals v. v dot equals the net force, T minus m g minus b v minus k times the quantity z minus z sub s, plus d, all divided by m.',
        },
        {
          type: 'list',
          items: [
            '**Altitude rate** ($\\dot z = v$): The speed of position change is velocity. If you are moving upward at 2 m/s, your altitude increases at 2 metres per second.',
            '**Velocity rate** ($\\dot v = a$): The speed of velocity change is acceleration, calculated as net force divided by mass.',
          ],
        },
        {
          type: 'p',
          text: 'These two equations tell the computer how to advance time forward. Over a brief time slice $\\Delta t$, the approximate change in any quantity is simply **rate × interval**. For example, $z_{new} \\approx z + v\\,\\Delta t$ and $v_{new} \\approx v + a\\,\\Delta t$. This manual calculation is an {{euler|Euler step}}, a fast approximation. The simulator uses these exact same rate equations with a higher-order numerical method ([RK4](https://en.wikipedia.org/wiki/Runge%E2%80%93Kutta_methods)) for smooth precision.',
        },
        { type: 'widget', widget: 'euler-step' },
      ],
    },
    {
      id: 'm1-equilibrium',
      title: 'Equilibrium and deviations',
      phase: 'learn',
      blocks: [
        {
          type: 'p',
          text: 'When programming a flight controller, you don’t want your control algorithms recalculating baseline gravity from scratch every millisecond. Instead, you supply a steady baseline thrust to hold the vehicle in a steady hover, and design the controller to calculate only the small **adjustments** needed to correct errors.',
        },
        {
          type: 'p',
          text: 'First, choose an {{z0|operating altitude}} $z_0$ where you want to hover steadily with zero velocity and no disturbance. The thrust required to maintain this balance is the {{T0|equilibrium thrust}}:',
        },
        {
          type: 'math',
          tex: '\\term{T0}{T_0} = \\term{m}{m}\\,\\term{g}{g} + \\term{k}{k}\\,(\\term{z0}{z_0} - \\term{zs}{z_s})',
          aria: 'T sub zero equals m g plus k times the quantity z sub zero minus z sub s.',
        },
        {
          type: 'p',
          text: 'In our default preset, the target hover height matches the spring resting height ($z_0 = z_s = 2\\text{ m}$), so the spring exerts zero force and $T_0 = m\\,g = 9.81\\text{ N}$.',
        },
        {
          type: 'p',
          text: 'Next, instead of tracking raw altitude $z$ and total thrust $T$, measure everything as a **deviation** from this hover baseline:',
        },
        {
          type: 'math',
          tex: '\\term{q}{q} = \\term{z}{z} - \\term{z0}{z_0} \\qquad \\term{u}{u} = \\term{T}{T} - \\term{T0}{T_0}',
          aria: 'q equals z minus z sub zero. u equals T minus T sub zero.',
        },
        {
          type: 'list',
          items: [
            '**Altitude deviation** ({{q}}): How far above ($q > 0$) or below ($q < 0$) the hover height you are. Because $z_0$ is constant, its rate of change is simply velocity: $\\dot q = \\dot z = v$.',
            '**Thrust deviation** ({{u}}): How much extra ($u > 0$) or less ($u < 0$) thrust the controller commands relative to hover thrust $T_0$.',
          ],
        },
        {
          type: 'p',
          text: 'Now substitute $z = z_0 + q$ and $T = T_0 + u$ into our motion equations. Because $T_0$ was chosen specifically to cancel weight and resting spring force, the constant $m\\,g$ and spring offset vanish completely:',
        },
        {
          type: 'math',
          tex: '\\begin{aligned}\\term{dot}{\\dot q} &= \\term{v}{v} \\\\ \\term{dot}{\\dot v} &= -\\frac{\\term{k}{k}}{\\term{m}{m}}\\,\\term{q}{q} - \\frac{\\term{b}{b}}{\\term{m}{m}}\\,\\term{v}{v} + \\frac{1}{\\term{m}{m}}\\,\\term{u}{u} + \\frac{1}{\\term{m}{m}}\\,\\term{disturbance}{d}\\end{aligned}',
          aria: 'q dot equals v. v dot equals minus k over m times q, minus b over m times v, plus one over m times u, plus one over m times d.',
        },
        {
          type: 'callout',
          tone: 'key',
          title: 'Gravity is balanced, not removed',
          text: 'You might wonder where gravity went in the equations above. Gravity has not been removed; it is still actively pulling down in the simulator! It disappears from these equations because our baseline hover thrust ($T_0$) already balances it in the background. By tracking deviations from hover ($q$ and $u$), every term becomes a simple constant multiplied by a single variable, which is what {{linearized}} means. For our spring-damper vehicle, this math is exact (away from the ground and thrust limits); for a real multirotor, it is a close approximation near level hover.',
        },
      ],
    },
    {
      id: 'm1-matrices',
      title: 'Grouping into a state vector and matrices',
      phase: 'learn',
      blocks: [
        {
          type: 'p',
          text: 'Managing two scalar equations on paper is straightforward for our 1D vehicle. But real-world drones operate in 3D: 3 position axes, 3 velocities, 3 orientation angles, and 3 rotation rates, a total of 12 or more coupled equations! Control engineers handle this complexity by packaging all variables into a **state vector** and all coefficients into **matrices**.',
        },
        {
          type: 'p',
          text: 'First, define the {{state}}: the minimal set of numbers that, together with future control inputs, completely dictates the future motion. For our vehicle, the state contains altitude deviation $q$ and velocity $v$. We stack them in a fixed order into a column {{vector}} $x$, with time derivative $\\dot x$:',
        },
        {
          type: 'math',
          tex: '\\term{state}{x} = \\begin{bmatrix} \\term{q}{q} \\\\ \\term{v}{v} \\end{bmatrix} = [\\term{q}{q},\\ \\term{v}{v}]^{\\term{transpose}{T}} \\qquad \\term{xdot}{\\dot x} = \\begin{bmatrix} \\term{dot}{\\dot q} \\\\ \\term{dot}{\\dot v} \\end{bmatrix}',
          aria: 'x is the column vector with q on top and v below. x dot is the column of their rates: q dot on top, v dot below.',
        },
        {
          type: 'p',
          text: '*(Note: The superscript {{transpose|ᵀ}} simply means "write this horizontal row as a vertical column").*',
        },
        {
          type: 'p',
          text: 'With this notation, both differential equations combine into a single, universal state-space equation:',
        },
        {
          type: 'math',
          tex: '\\term{xdot}{\\dot x} = \\term{A}{A}\\,\\term{state}{x} + \\term{B}{B}\\,\\term{u}{u} + \\term{E}{E}\\,\\term{disturbance}{d}',
          aria: 'x dot equals A times x, plus B times u, plus E times d.',
        },
        {
          type: 'p',
          text: 'The coefficients are organized into tables called {{matrix|matrices}}:',
        },
        {
          type: 'math',
          tex: '\\term{A}{A} = \\begin{bmatrix} 0 & 1 \\\\ -\\tfrac{\\term{k}{k}}{\\term{m}{m}} & -\\tfrac{\\term{b}{b}}{\\term{m}{m}} \\end{bmatrix} \\quad \\term{B}{B} = \\begin{bmatrix} 0 \\\\ \\tfrac{1}{\\term{m}{m}} \\end{bmatrix} \\quad \\term{E}{E} = \\begin{bmatrix} 0 \\\\ \\tfrac{1}{\\term{m}{m}} \\end{bmatrix}',
          aria: 'A has first row 0, 1, and second row minus k over m, minus b over m. B is the column 0, one over m. E is also the column 0, one over m.',
        },
        {
          type: 'list',
          items: [
            '**System matrix** ($\\term{A}{A}$): Represents internal dynamics - how position and velocity naturally evolve on their own due to velocity, spring tension, and damping.',
            '**Input matrix** ($\\term{B}{B}$): Represents how our control command $u$ (extra thrust) affects the rates of change.',
            '**Disturbance matrix** ($\\term{E}{E}$): Represents how external disturbance forces $d$ affect the rates of change.',
          ],
        },
        {
          type: 'p',
          text: 'To see how this matrix multiplication reproduces our two scalar equations, multiply each row of coefficients by the entries of vector $x$ and add them up:',
        },
        {
          type: 'list',
          items: [
            '**Row 1 (Kinematics)**: $\\dot q = 0 \\cdot q + 1 \\cdot v + 0 \\cdot u + 0 \\cdot d = v$. The rate of change of altitude is velocity. Thrust and disturbances cannot change altitude instantly; they must accelerate velocity first.',
            '**Row 2 (Dynamics)**: $\\dot v = -\\frac{k}{m}q - \\frac{b}{m}v + \\frac{1}{m}u + \\frac{1}{m}d$. This reproduces Newton’s force balance divided by mass.',
          ],
        },
        { type: 'widget', widget: 'matrix-rows' },
        {
          type: 'p',
          text: 'Finally, controllers need to know what the sensors actually measure. Real systems rarely measure every state variable directly. The **output equation** defines what an ideal sensor reports:',
        },
        {
          type: 'math',
          tex: '\\term{y}{y} = \\term{C}{C}\\,\\term{state}{x} + \\term{Dmat}{D}\\,\\term{u}{u} \\qquad \\term{C}{C} = \\begin{bmatrix} 1 & 0 \\end{bmatrix} \\qquad \\term{Dmat}{D} = 0',
          aria: 'y equals C times x plus D times u, where C is the row 1, 0, and D is 0. So y equals q.',
        },
        {
          type: 'p',
          text: 'Multiplying this out gives $y = 1 \\cdot q + 0 \\cdot v = q$. Our sensor measures altitude deviation $q$ directly, so true altitude is $z_0 + y$. The direct-feedthrough matrix {{Dmat|D}} is zero because motor thrust does not instantly alter sensor readings without moving the vehicle first. In practice, a physical {{sensor}} also adds measurement {{n|noise}} ($y_m = y + n$).',
        },
      ],
    },
    {
      id: 'm1-predict',
      title: 'Predict',
      phase: 'predict',
      blocks: [
        {
          type: 'p',
          text: 'Before running the simulation, test your physical intuition with a quick thought experiment using the force balance.',
        },
        {
          type: 'predict',
          id: 'm1-p1',
          prompt:
            'The tethered vehicle (k = 2 N/m, $z_s$ = 2 m) starts at rest at z = 2.5 m with thrust 12 N. Which way does it accelerate at the first instant?',
          options: [
            { id: 'up', text: 'Up' },
            { id: 'down', text: 'Down' },
            { id: 'none', text: 'Not at all' },
          ],
          reveal:
            'Net force = 12 N (thrust) − 9.81 N (gravity) − 1·0 (damping) − 2·(2.5 − 2) N (spring) + 0 N (disturbance) = +1.19 N. Because the net force is positive upward, the vehicle accelerates upward at a = +1.19 m/s².',
        },
      ],
    },
    {
      id: 'm1-lab',
      title: 'Step through a snapshot',
      phase: 'experiment',
      blocks: [
        {
          type: 'p',
          text: 'Now that you have seen the force balance and state equations on paper, put them to the test in the live simulator to observe how forces, rates, and positions evolve over time.',
        },
        {
          type: 'h',
          text: 'Physical Forces in Dynamic Motion',
        },
        {
          type: 'p',
          text: '**Purpose**: Trace how net force dictates instantaneous acceleration in a physical dynamical system. The vehicle is attached to a tether spring ($k = 2\\text{ N/m}$, resting altitude $z_s = 2\\text{ m}$) and subject to aerodynamic damping ($b = 1\\text{ N}\\cdot\\text{s/m}$). Released from rest ($v = 0$) at $z = 2.5\\text{ m}$ with thrust held constant at 12 N (feedforward off), the craft experiences competing upward and downward forces that evolve dynamically with position and velocity.',
        },
        {
          type: 'p',
          text: 'Before hitting Play, you can click **Step** a few times to advance time by discrete $\\Delta t$ slices to observe the numerical Euler integration firsthand.',
        },
        { type: 'lab' },
      ],
    },
    {
      id: 'm1-explain',
      title: 'Explain',
      phase: 'explain',
      blocks: [
        {
          type: 'p',
          text: 'Explaining a mathematical model in plain language is one of the best ways to verify that you understand the physics behind it.',
        },
        {
          type: 'reflect',
          id: 'm1-r1',
          prompt: 'In your own words, what does each row of $\\dot x = A x + B u + E d$ say physically?',
        },
      ],
    },
  ],
  lab: {
    id: 'm1-lab',
    version: 1,
    instructions: 'Load the 10 s spring-mass snapshot run: click Step to observe discrete updates, then press Play or Run to end to record the full trajectory.',
    steps: [
      {
        id: 'm1-snapshot',
        title: 'Constant thrust from z = 2.5 m',
        presetId: 'spring-snapshot',
        instructions:
          'Load the step, then click Step a few times to advance in discrete 0.005 s slices (or press Play to run continuously). Watch how 12 N thrust overcomes gravity (9.81 N) and the downward spring pull (-1.0 N) to accelerate the vehicle upward, while drag increases to resist rising motion.',
        permitted: [],
        requires: 'A complete 10 s run.',
      },
    ],
  },
  gate: {
    id: 'g1',
    version: 1,
    title: 'Check: forces and state',
    questions: [
      {
        id: 'g1-q1',
        version: 1,
        kind: 'numeric',
        binds: ['m1-snapshot'],
        prompt: (ctx) =>
          `Here is the sample at t = ${f(snap(ctx).t)} s from your run, with the plant parameters. Calculate the acceleration $a$ using $m a = T - m g - b v - k(z - z_s) + d$. Round to 2 decimal places.`,
        table: (ctx) => {
          const s = snap(ctx);
          const p = plantOf(ctx);
          return {
            caption: 'Snapshot and parameters',
            headers: ['z (m)', 'v (m/s)', 'T (N)', 'd (N)', 'm (kg)', 'g (m/s²)', 'b (N·s/m)', 'k (N/m)', 'z_s (m)'],
            rows: [[f(s.z, 3), f(s.v, 3), f(s.T, 2), f(s.d, 2), p.m, p.g, p.b, p.k, p.zs]],
          };
        },
        unit: 'm/s²',
        answer: accel,
        tolerance: 0.02,
        misconceptions: [
          {
            test: (x, ctx) => {
              const s = snap(ctx);
              const p = plantOf(ctx);
              return Math.abs(x - (s.T - p.m * p.g - p.b * s.v + s.d) / p.m) < 0.02;
            },
            feedback: 'Close. Did you include the spring force $-k(z - z_s)$?',
          },
          {
            test: (x, ctx) => {
              const s = snap(ctx);
              const p = plantOf(ctx);
              return Math.abs(x - (s.T - p.m * p.g - p.k * (s.z - p.zs) + s.d) / p.m) < 0.02;
            },
            feedback: 'Did you include the damping force $-b v$? Check the sign of v.',
          },
          {
            test: (x, ctx) => {
              const s = snap(ctx);
              const p = plantOf(ctx);
              return Math.abs(x - (s.T - p.m * p.g + p.b * s.v - p.k * (s.z - p.zs) + s.d) / p.m) < 0.02;
            },
            feedback: 'Check the sign of the damping term: it is $-b\\,v$, so it opposes the velocity.',
          },
          {
            test: (x, ctx) => {
              const s = snap(ctx);
              const p = plantOf(ctx);
              return Math.abs(x - (s.T + p.m * p.g - p.b * s.v - p.k * (s.z - p.zs) + s.d) / p.m) < 0.05;
            },
            feedback: 'Gravity pulls down, so it enters as $-m g$.',
          },
        ],
        hint: 'Work out each force separately (with sign), add them, then divide by m.',
        explanation: 'Net force ÷ mass = acceleration. The simulator computes exactly this at every step.',
        review: 'm1-forces',
      },
      {
        id: 'g1-q2',
        version: 1,
        kind: 'numeric',
        binds: ['m1-snapshot'],
        prompt: () => `For the same sample, with $z_0$ = 2 m, what is the state entry $q$ (altitude deviation)?`,
        table: (ctx) => ({
          caption: 'Snapshot',
          headers: ['z (m)', 'v (m/s)'],
          rows: [[f(snap(ctx).z, 3), f(snap(ctx).v, 3)]],
        }),
        unit: 'm',
        answer: (ctx) => snap(ctx).z - 2,
        tolerance: 0.002,
        misconceptions: [{ test: (x, ctx) => Math.abs(x - snap(ctx).z) < 0.002, feedback: 'You reported z itself. q is measured from $z_0$: q = z − z₀.' }],
        hint: '$q = z - z_0$.',
        explanation: 'q is altitude measured from the operating point.',
        review: 'm1-equilibrium',
      },
      {
        id: 'g1-q3',
        version: 1,
        kind: 'choice',
        binds: [],
        prompt: () => 'Which pair makes up the plant state x?',
        options: [
          { id: 'qv', text: 'deviation q and velocity v' },
          { id: 'zT', text: 'altitude z and thrust T', feedback: 'Thrust is an input you choose, not part of the state.' },
          { id: 'qu', text: 'deviation q and thrust deviation u', feedback: 'u is the input. The state is what the physics remembers.' },
          { id: 'va', text: 'velocity v and acceleration a', feedback: 'Acceleration is set by the state and inputs at each instant. It is a rate, in $\\dot x$.' },
        ],
        correct: () => 'qv',
        hint: 'Which two numbers do you need, together with future thrust, to predict the future?',
        explanation: '$x = [q, v]^T$.',
        review: 'm1-matrices',
      },
      {
        id: 'g1-q4',
        version: 1,
        kind: 'match',
        binds: [],
        prompt: () => 'Match each row of the matrix equations with the scalar equation it expands to.',
        items: [
          { id: 'r1', text: 'Row 1 of $A x + B u + E d$' },
          { id: 'r2', text: 'Row 2 of $A x + B u + E d$' },
          { id: 'out', text: 'The single row of $C x + D u$' },
        ],
        choices: [
          { id: 'qdot', text: '$\\dot q = v$' },
          { id: 'vdot', text: '$\\dot v = -\\frac{k}{m} q - \\frac{b}{m} v + \\frac{1}{m} u + \\frac{1}{m} d$' },
          { id: 'y', text: '$y = q$' },
          { id: 'wrong', text: '$\\dot v = v$' },
        ],
        correct: () => ({ r1: 'qdot', r2: 'vdot', out: 'y' }),
        hint: 'Row 1 of A is [0, 1]: 0·q + 1·v. Row 1 of B and E is 0.',
        explanation: 'Each matrix row is one scalar equation written compactly.',
        review: 'm1-matrices',
      },
      {
        id: 'g1-q5',
        version: 1,
        kind: 'choice',
        binds: [],
        prompt: () => 'The tethered vehicle hovers still at $z_0 = z_s = 2$ m. What balances gravity?',
        options: [
          { id: 'T0', text: 'The equilibrium thrust $T_0 = m g$' },
          { id: 'spring', text: 'The spring', feedback: 'At $z = z_s$ the spring exerts zero force.' },
          { id: 'damping', text: 'Damping', feedback: 'Damping is $-b v$, which is zero when v = 0.' },
          { id: 'removed', text: 'Nothing: the linearized model removes gravity', feedback: 'Gravity is still there. $T_0$ cancels it. The terms vanish from the deviation equations because of the cancel.' },
        ],
        correct: () => 'T0',
        hint: 'List the forces at z = z_s, v = 0.',
        explanation: 'At hover the rotors supply exactly the weight: $T_0 = m g + k(z_0 - z_s) = m g$ here.',
        review: 'm1-equilibrium',
      },
      {
        id: 'g1-q6',
        version: 1,
        kind: 'numeric',
        binds: [],
        prompt: () =>
          'Suppose you chose the operating altitude $z_0$ = 2.5 m instead (tethered preset: m = 1 kg, g = 9.81 m/s², k = 2 N/m, $z_s$ = 2 m). What equilibrium thrust $T_0$ is needed?',
        unit: 'N',
        answer: () => 9.81 + 2 * 0.5,
        tolerance: 0.01,
        misconceptions: [
          { test: (x) => Math.abs(x - 9.81) < 0.01, feedback: 'At 2.5 m the spring is stretched and pulls down. Include $k(z_0 - z_s)$.' },
          { test: (x) => Math.abs(x - 8.81) < 0.01, feedback: 'The spring pulls down above $z_s$, so the rotors need more thrust.' },
        ],
        hint: '$T_0 = m g + k(z_0 - z_s)$.',
        explanation: '9.81 + 2 × 0.5 = 10.81 N.',
        review: 'm1-equilibrium',
      },
    ],
  },
};
